import React, { useEffect, useRef, useState } from 'react';
import { Dashboard as DashboardLayout } from 'layouts';
import {
  AppBar,
  Box,
  Breadcrumbs,
  Button,
  Grid2,
  Link,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { TrendDemandChart } from './components/TrendDemandChart';
import { NavLink, useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  showTrendAverage,
  showTrendCo2,
  showTrendConsume,
  showTrendDemand,
  showTrendLoadFactor,
} from 'js/actions/trendActions';
import { TrendConsumeChart } from './components/TrendConsumeChart';
import { AverageChart } from './components/Average';
import { Co2Chart } from './components/Co2Chart';
import { LoadFactorChart } from './components/LoadFactorChart';
import { Dashboard, Download } from '@mui/icons-material';
import { isAllowed } from 'helpers';
import theme from 'theme';
import { useIntl } from 'react-intl';
import { getHistoricalReport } from 'js/actions/chargeDashboardActions';
import { history } from 'helpers/history';

const ChargeDashboardTrend = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { trendConsume, trendAverage, trendCo2, trendDemand, trendLoadFactor } =
    useSelector((state) => state.trend);
  const { historicalReport } = useSelector((state) => state.chargeDashboard);
  const { chargeId } = useParams();
  const [trend, setTrend] = useState(9);
  const { meter, group, charge } = useLocation().state;
  const { user } = useSelector((state) => state.user);
  const dashboardRef = useRef();

  const startDate = () => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${month}${year}`;
  };

  useEffect(() => {
    dispatch(showTrendConsume(chargeId, startDate()));
    dispatch(showTrendAverage(chargeId, startDate()));
    dispatch(showTrendCo2(chargeId, startDate()));
    dispatch(showTrendDemand(chargeId, startDate()));
    dispatch(showTrendLoadFactor(chargeId, startDate()));
    dispatch(getHistoricalReport(chargeId));
  }, [chargeId]);

  const handleNavigate = () => {
    history.back();
  };

  const handleReport = () => {
    window.open(historicalReport, '_blank');
  };

  return (
    <DashboardLayout>
      <Grid2 container={true} spacing={4} p={4} ref={dashboardRef}>
        <Box
          width="100%"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box display="flex" flexDirection="column" alignItems="start">
            <Typography variant="h3" sx={{ color: '#A53693', mb: 2 }}>
              {intl.formatMessage({
                id: 'app.chargeTrend.board.historicDashboard.title',
              })}
            </Typography>
            <Box display="flex">
              {isAllowed(user.permissions, ['corporative_read']) && meter && (
                <Breadcrumbs
                  arial-label="breadcrumb"
                  separator=":"
                  sx={{ marginRight: theme.spacing(1) }}
                >
                  <Link
                    color="inherit"
                    component={NavLink}
                    sx={{ color: '#A53693', textDecoration: 'none' }}
                    to={`/corporative/${meter?.corporative_id}`}
                    state={meter}
                  >
                    {intl.formatMessage({ id: 'app.groups.nav.meters' })}
                  </Link>
                  <Link
                    color="inherit"
                    component={NavLink}
                    sx={{ textDecoration: 'none' }}
                    to={`/meter/${meter.id}`}
                    state={meter}
                  >
                    {meter
                      ? meter.name
                      : intl.formatMessage({ id: 'app.groups.nav.groupLab2' })}
                  </Link>
                  <Typography sx={{ fontWeight: 600, color: '#00AFEF' }}>
                    {' '}
                    |{' '}
                  </Typography>
                </Breadcrumbs>
              )}
              {meter && (
                <Breadcrumbs
                  arial-label="breadcrumb"
                  separator=":"
                  sx={{ marginLeft: theme.spacing(1), color: '#A53693' }}
                >
                  <Link
                    color="inherit"
                    component={NavLink}
                    to={`/meter/${meter?.id}`}
                    state={meter}
                    sx={{ textDecoration: 'none' }}
                  >
                    {intl.formatMessage({ id: 'app.charges.nav.groupLab2' })}
                  </Link>
                  <Link
                    sx={{ textDecoration: 'none', color: '#66788A' }}
                    component={NavLink}
                    to={`/group/${group.id}`}
                    state={{
                      group: group,
                      meter: meter,
                    }}
                  >
                    {group
                      ? group.name
                      : intl.formatMessage({
                          id: 'app.charges.nav.chargeLab2',
                        })}
                  </Link>
                  <Typography sx={{ fontWeight: 600, color: '#00AFEF' }}>
                    {' '}
                    |{' '}
                  </Typography>
                </Breadcrumbs>
              )}
              <Breadcrumbs
                arial-label="breadcrumb"
                separator=":"
                sx={{ marginLeft: theme.spacing(1), color: '#A53693' }}
              >
                <Link
                  color="inherit"
                  component={NavLink}
                  to={`/group/${group ? group.id : charge?.charge?.group_id}`}
                  state={{
                    group: group,
                    meter: meter,
                  }}
                  sx={{ textDecoration: 'none' }}
                >
                  {intl.formatMessage({
                    id: 'app.reports.hourReports.chargeLbl',
                  })}
                </Link>
                <Typography variant="h5">
                  {charge
                    ? charge.name
                    : intl.formatMessage({ id: 'app.groups.nav.groupLab2' })}
                </Typography>
              </Breadcrumbs>
            </Box>
          </Box>
          <Box display="flex" gap={2}>
            {historicalReport && (
              <Button
                variant="contained"
                color="secondary"
                startIcon={<Download />}
                onClick={handleReport}
              >
                {intl.formatMessage({
                  id: 'app.chargeTrend.board.historicDashboard.DownloadButton',
                })}
              </Button>
            )}
            <Button
              variant="contained"
              color="secondary"
              startIcon={<Dashboard />}
              onClick={handleNavigate}
            >
              Dashboard
            </Button>
          </Box>
        </Box>
        <Grid2 size={12} position="sticky" top={64} zIndex={1000}>
          <AppBar position="static" color="secondary">
            <Tabs
              value={trend}
              centered
              textColor="inherit"
              indicatorColor="inherit"
              variant="fullWidth"
              sx={{
                '& .MuiTabs-indicator': { backgroundColor: 'white' },
              }}
            >
              <Tab
                label={intl.formatMessage(
                  { id: 'app.chargeTrend.board.historicDashboard.monthTitles' },
                  { month: 3 }
                )}
                value={3}
                onClick={() => setTrend(3)}
                sx={{
                  '&.Mui-selected': {
                    color: 'white',
                    backgroundColor: 'rgb(0, 146, 199)',
                    borderRadius: 1,
                  },
                  '&:hover': {
                    backgroundColor: 'rgb(0, 146, 199)',
                    borderRadius: 1,
                  },
                }}
              />
              <Tab
                label={intl.formatMessage(
                  { id: 'app.chargeTrend.board.historicDashboard.monthTitles' },
                  { month: 6 }
                )}
                value={6}
                onClick={() => setTrend(6)}
                sx={{
                  '&.Mui-selected': {
                    color: 'white',
                    backgroundColor: 'rgb(0, 146, 199)',
                    borderRadius: 1,
                  },
                  '&:hover': {
                    backgroundColor: 'rgb(0, 146, 199)',
                    borderRadius: 1,
                  },
                }}
              />
              <Tab
                label={intl.formatMessage(
                  { id: 'app.chargeTrend.board.historicDashboard.monthTitles' },
                  { month: 9 }
                )}
                value={9}
                onClick={() => setTrend(9)}
                sx={{
                  '&.Mui-selected': {
                    color: 'white',
                    backgroundColor: 'rgb(0, 146, 199)',
                    borderRadius: 1,
                  },
                  '&:hover': {
                    backgroundColor: 'rgb(0, 146, 199)',
                    borderRadius: 1,
                  },
                }}
              />
              <Tab
                label={intl.formatMessage(
                  { id: 'app.chargeTrend.board.historicDashboard.monthTitles' },
                  { month: 13 }
                )}
                value={13}
                onClick={() => setTrend(13)}
                sx={{
                  '&.Mui-selected': {
                    color: 'white',
                    backgroundColor: 'rgb(0, 146, 199)',
                    borderRadius: 1,
                  },
                  '&:hover': {
                    backgroundColor: 'rgb(0, 146, 199)',
                    borderRadius: 1,
                  },
                }}
              />
            </Tabs>
          </AppBar>
        </Grid2>
        <Grid2 size={{ lg: 6, md: 12 }}>
          <TrendConsumeChart data={trendConsume} trend={trend} />
        </Grid2>
        <Grid2 size={{ lg: 6, md: 12 }}>
          <TrendDemandChart data={trendDemand} trend={trend} />
        </Grid2>
        <Grid2 size={12}>
          <LoadFactorChart data={trendLoadFactor} trend={trend} />
        </Grid2>
        <Grid2 size={12}>
          <AverageChart data={trendAverage} trend={trend} />
        </Grid2>
        <Grid2 size={12}>
          <Co2Chart data={trendCo2} trend={trend} />
        </Grid2>
      </Grid2>
    </DashboardLayout>
  );
};

export default ChargeDashboardTrend;
